import { useContext, useEffect, useMemo } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { useQuery } from '@apollo/client';
import { format, getDay, parse, startOfWeek } from 'date-fns';
import { enUS } from 'date-fns/locale/en-US';
import * as R from 'ramda';

import { Progress } from '@atom/components/common/mui';
import { GET_MONTHLY_APPOINTMENTS } from '@atom/graph/appointments';
import { useNavigation } from '@atom/hooks/useNavigation';
import {
  CalendarView,
  useWorkOrdersFilters,
} from '@atom/hooks/useWorkOrdersFilters';
import colors from '@atom/styles/colors';
import fonts from '@atom/styles/fonts';
import {
  AppointmentsByDay,
  AppointmentsMap,
  MonthlyAppointmentsConnection,
} from '@atom/types/appointments';
import { WorkOrdersConnectionInput } from '@atom/types/work';

import WorkOrdersContext from '../WorkOrdersContext';

import WeekViewWrapper from './WeekViewWrapper';
import WorkOrdersCalendarCell from './WorkOrdersCalendarCell';
import WorkOrdersCalendarToolbar from './WorkOrdersCalendarToolbar';

import classes from '../workOrders.module.css';

export const getCalendarDateKey = (date: number | Date) => format(date, 'MMdd');

const styles = {
  calendar: {
    height: 'calc(100vh - 110px)',
  },
  event: {
    color: colors.neutral.dim,
    fontSize: fonts.sm,
    borderRadius: '2px',
    height: '28px',
    display: 'flex',
    alignItems: 'center',
  },
  progress: {
    height: '100%',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
};

const views = {
  week: WeekViewWrapper,
  month: true,
};

const MONTHLY_APPOINTMENTS_OMITTED_PARAMS: Array<
  keyof WorkOrdersConnectionInput
> = ['page', 'limit', 'sortBy'];

const locales = {
  'en-US': enUS,
};

// Needed for react-big-calendar
// https://github.com/jquense/react-big-calendar/blob/master/README.md
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

const WorkOrdersCalendar = () => {
  const [, { navigate }] = useNavigation();
  const { workOrdersInput } = useWorkOrdersFilters();
  const { input, setInput, workOrders } = useContext(WorkOrdersContext);

  const { data, loading, refetch } = useQuery<
    { appointments: MonthlyAppointmentsConnection },
    { input: WorkOrdersConnectionInput }
  >(GET_MONTHLY_APPOINTMENTS, {
    variables: {
      input: {
        ...R.omit(MONTHLY_APPOINTMENTS_OMITTED_PARAMS, workOrdersInput),
        appointmentSort: true,
      },
    },
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    if (input.calendarView === CalendarView.MONTH) {
      refetch();
    }
  }, [workOrdersInput, workOrders]);

  const appointmentsByDay: AppointmentsByDay[] = R.pathOr(
    null,
    ['monthlyAppointments', 'appointments'],
    data,
  );

  // Create map of dates
  // - using MMDD as keys to ensure unique keys for each date
  const appointmentsMap: AppointmentsMap = useMemo(() => {
    if (!appointmentsByDay) {
      return {};
    }
    return appointmentsByDay.reduce(
      (acc: AppointmentsMap, appointments) => ({
        ...acc,
        [getCalendarDateKey(appointments.date)]: appointments,
      }),
      {},
    );
  }, [appointmentsByDay]);

  const onSelectEvent = event => {
    navigate(`/workOrders/${event.title}`);
  };

  if (loading) {
    return <Progress size={75} style={styles.progress} />;
  }

  return (
    <div className={classes.calendarContainer}>
      <Calendar
        localizer={localizer}
        views={views}
        startAccessor="start"
        endAccessor="end"
        onSelectEvent={onSelectEvent}
        style={styles.calendar}
        defaultDate={new Date(input.calendarViewDate)}
        defaultView={input.calendarView}
        onNavigate={calendarViewDate =>
          setInput({ ...input, calendarViewDate: calendarViewDate.valueOf() })
        }
        onView={calendarView => setInput({ ...input, calendarView })}
        components={{
          toolbar: props => (
            <WorkOrdersCalendarToolbar {...props} loading={loading} />
          ),
          dateCellWrapper: props => (
            <WorkOrdersCalendarCell
              {...props}
              loading={loading}
              appointmentsMap={appointmentsMap}
            />
          ),
          dateHeader: () => <></>,
        }}
      />
    </div>
  );
};

export default WorkOrdersCalendar;
